/**
 * Utility functions for calculations in the clearance calculator
 */

import { CalculatorResults } from './calculatorState';

// Define type for height to clearance mapping
export interface HeightClearanceMap {
  [height: number]: number;
}

// Define input parameters for calculations (legacy compatibility)
export interface CalculationParams {
  trackType: 'curve' | 'tangent';
  direction: 'inside' | 'outside' | 'IN' | 'OUT';
  division: 'A Division' | 'B Division';
  H: number | string;
  D: number | string;
  MO: number | string;
  SUPER: number | string;
  A_height_to_clearance: HeightClearanceMap;
  B_height_to_clearance: HeightClearanceMap;
  A_above_gor_to_height?: HeightClearanceMap;
  B_above_gor_to_height?: HeightClearanceMap;
}

// Re-export for backwards compatibility
export type CalculationResults = CalculatorResults;

// Define state management (legacy compatibility)
export interface CalculationState {
  state: 'input' | 'results';
  H: number | null;
  D: number | null;
  MO: number | null;
  SUPER: number | null;
}

// Interface for state setters
export interface CalculationStateSetters {
  setState: React.Dispatch<React.SetStateAction<'input' | 'results'>>;
  setH: React.Dispatch<React.SetStateAction<number | null>>;
  setD: React.Dispatch<React.SetStateAction<number | null>>;
  setMO: React.Dispatch<React.SetStateAction<number | null>>;
  setSUPER: React.Dispatch<React.SetStateAction<number | null>>;
  setR: React.Dispatch<React.SetStateAction<number>>;
  setSE: React.Dispatch<React.SetStateAction<number>>;
  setEE: React.Dispatch<React.SetStateAction<number>>;
  setCE: React.Dispatch<React.SetStateAction<number>>;
  setLLLEMinReq: React.Dispatch<React.SetStateAction<number>>;
  setLLLEClearance: React.Dispatch<React.SetStateAction<number>>;
  setClearance: React.Dispatch<React.SetStateAction<number>>;
}

// Interface for calculation context
export interface CalculationContext {
  trackType: 'curve' | 'tangent';
  direction: 'inside' | 'outside';
  division: 'A Division' | 'B Division';
  A_height_to_clearance: HeightClearanceMap;
  B_height_to_clearance: HeightClearanceMap;
  A_above_gor_to_height?: HeightClearanceMap;
  B_above_gor_to_height?: HeightClearanceMap;
}

// Interface for numeric input handlers
interface NumericChangeHandlerOptions {
  minValue: number;
  maxValue: number;
  emptyValue: NumericValue;
  precision: number;
  allowNegative: boolean;
}

/**
 * Creates a handler for toggling the sign of a numeric value
 * @param setter - The state setter function
 * @param currentValue - The current numeric value
 * @param minValue - Minimum allowed value
 * @param maxValue - Maximum allowed value
 * @param onWarning - Optional callback for when operation is blocked
 * @returns void
 */
export const toggleSignHandler = (
  setter: React.Dispatch<React.SetStateAction<number | null>>,
  currentValue: number | null,
  minValue: number,
  maxValue: number,
  onWarning?: () => void
): void => {
  if (currentValue === null) {
    return;
  }
  
  // Multiply by -1 to toggle sign
  const newValue = -currentValue;
  
  // Check if new value is within allowed range
  if (newValue >= minValue && newValue <= maxValue) {
    setter(newValue);
  } else {
    // Value would be out of bounds, trigger warning if callback provided
    if (onWarning) {
      onWarning();
    }
  }
};

type NumericValue = number | null;

/**
 * Creates a handler for numeric input with validation
 * @param setter - The state setter function
 * @param minValue - Minimum allowed value (inclusive)
 * @param maxValue - Maximum allowed value (inclusive)
 * @param precision - Maximum decimal places allowed
 * @returns A handler function for IonInput (use with onIonInput)
 */
export const createNumericInputHandler = (
  setter: React.Dispatch<React.SetStateAction<number | null>>,
  minValue: number = 0,
  maxValue: number = 1000,
  precision: number = 3
): ((e: Event) => void) => {
  // Convert the simple parameters to the options object format
  const options: NumericChangeHandlerOptions = {
    minValue,
    maxValue,
    emptyValue: null,
    precision,
    allowNegative: minValue < 0
  };

  // Track the last *accepted* display text and numeric value
  let lastText: string = '';
  let lastNumeric: NumericValue = options.emptyValue;

  const setVisualValue = (e: Event, text: string) => {
    // Works for Ionic's web component and native input; set both to be safe
    const anyEvt = e as any;
    const tgt = (anyEvt?.target ?? anyEvt?.currentTarget) as any;
    if (tgt && typeof tgt === 'object') {
      try {
        // HTMLIonInputElement exposes a .value property that updates the shadow input
        if ('value' in tgt) tgt.value = text;
      } catch {
        /* noop */
      }
    }
  };

  const getIncomingText = (e: Event): string => {
    const anyEvt = e as any;
    // Works for Ionic v6/v7: prefer detail.value when present, fall back to target.value
    const detailVal = anyEvt?.detail?.value;
    const targetVal = (anyEvt?.target as HTMLInputElement | undefined)?.value;
    return (detailVal ?? targetVal ?? '').toString();
  };

  const clamp = (n: number, min: number, max: number) =>
    Math.min(max, Math.max(min, n));

  // Keep at most `precision` decimals WITHOUT rounding (strict edit-time precision)
  const trimToPrecision = (s: string, p: number) => {
    const i = s.indexOf('.');
    if (i === -1) return s;
    const intPart = s.slice(0, i);
    const fracPart = s.slice(i + 1, i + 1 + Math.max(0, p));
    return p > 0 ? `${intPart}.${fracPart}` : intPart;
  };

  // Validate characters: optional leading '-', digits, single '.'
  const buildPattern = (allowNegative: boolean) =>
    allowNegative ? /^-?\d*(?:\.\d*)?$/ : /^\d*(?:\.\d*)?$/;

  return (e: Event) => {
    const raw = getIncomingText(e);

    // Allow empty input
    if (raw === '') {
      lastText = '';
      lastNumeric = options.emptyValue;
      setVisualValue(e, '');
      setter(options.emptyValue);
      return;
    }

    // Reject characters outside the allowed grammar (including a 2nd '.')
    const allowedPattern = buildPattern(options.allowNegative);
    if (!allowedPattern.test(raw)) {
      // Refuse modification: restore previous value visually
      setVisualValue(e, lastText);
      // Numeric state remains the previously accepted value (no setter call needed)
      return;
    }

    // Enforce precision by trimming (prevents typing a 4th+ decimal digit)
    let nextText = trimToPrecision(raw, options.precision);

    // If trimming changed the input (e.g., attempted 4th fractional digit), reflect it immediately
    if (nextText !== raw) {
      setVisualValue(e, nextText);
    }

    // Attempt to parse a numeric value from the sanitized text
    // Cases like "-", "-.", or "." will yield NaN; allow them visually but don't update numeric state
    const parsed = Number(nextText);

    if (Number.isNaN(parsed)) {
      // Keep visual as typed/sanitized, but do not change numeric state
      lastText = nextText; // so we can revert to this if a second '.' is attempted next
      // Do not call setter; leave numeric state as-is
      return;
    }

    // Clamp to min/max if necessary; reflect clamp in the visual text too
    const clamped = clamp(parsed, options.minValue, options.maxValue);

    // Build a string for the clamped value, still respecting precision cap (no rounding beyond precision)
    // Using toString() avoids adding trailing zeros; then trim again to guard FP artifacts.
    let clampedText = clamped.toString();
    clampedText = trimToPrecision(clampedText, options.precision);

    // If clamping changed the numeric value or representation, reflect it immediately
    if (clamped !== parsed || clampedText !== nextText) {
      setVisualValue(e, clampedText);
      nextText = clampedText;
    }

    // Accept and persist
    lastText = nextText;
    lastNumeric = clamped;
    setter(clamped);
  };
};

/**
 * Format a number to a specified precision
 * @param number - The number to format
 * @param precision - Number of decimal places
 * @returns Formatted number
 */
export const formatNumber = (number: number | string, precision: number): number => {
  let num: number;
  try {
    // Handle both string and number inputs
    const parsedNumber = typeof number === 'string' ? parseFloat(number) : number;
    num = parseFloat(parsedNumber.toFixed(precision));
  } catch (error) {
    // If toFixed fails, try to convert directly
    num = typeof number === 'string' ? parseFloat(number) : number;
  }
  return num;
};

/**
 * Find the nearest key in a mapping object
 * @param map - Object with numeric keys
 * @param key - Key to find nearest value for
 * @returns Value of the nearest key
 */
export const findNearestKey = (map: HeightClearanceMap, key: number): number => {
  const keys = Object.keys(map).map(Number).sort((a, b) => a - b);
  
  if (keys.length === 0) {
    return 0; // Return default if map is empty
  }
  
  const nearestKey = keys.reduce((prev, curr) => 
    Math.abs(curr - key) < Math.abs(prev - key) ? curr : prev
  );
  
  return map[nearestKey];
};

/**
 * Calculate results for a given set of inputs
 * @param params - Input parameters and state
 * @returns Calculated values
 */
export const calculateResults = ({
  trackType,
  direction,
  division,
  H,
  D,
  MO,
  SUPER,
  A_height_to_clearance,
  B_height_to_clearance,
  A_above_gor_to_height,
  B_above_gor_to_height
}: CalculationParams): CalculationResults => {
  // Define calculations to update and later be set
  let r = 0, se = 0, ee = 0, ce = 0, minreq = 0, llleclearance = 0, clearance = 0;
  
  // Ensure numeric values for calculations
  const numH = typeof H === 'string' ? parseFloat(H) : H;
  const numD = typeof D === 'string' ? parseFloat(D) : D;
  const numMO = typeof MO === 'string' ? parseFloat(MO) : MO;
  const numSUPER = typeof SUPER === 'string' ? parseFloat(SUPER) : SUPER;

  // Special case: When GOR (D value) is between 0 and -28.25 (inclusive)
  if (numD <= 0 && numD >= -28.25) {
    // For above-train calculations, we directly use the map of GOR to minimum required height
    if (division === 'A Division' && A_above_gor_to_height) {
      if (Object.prototype.hasOwnProperty.call(A_above_gor_to_height, numD)) {
        minreq = A_above_gor_to_height[numD];
      } else {
        minreq = findNearestKey(A_above_gor_to_height, numD);
      }
    } else if (division === 'B Division' && B_above_gor_to_height) {
      if (Object.prototype.hasOwnProperty.call(B_above_gor_to_height, numD)) {
        minreq = B_above_gor_to_height[numD];
      } else {
        minreq = findNearestKey(B_above_gor_to_height, numD);
      }
    }
    
    // For above train calculations, LLLE clearance starts at the same value as minreq
    llleclearance = minreq;
    
    // At -28.25, the specific ballasted track and vertical curve considerations are handled in Calculator.tsx
    // through the ballasted track and vertical curve prompts, which will update llleclearance
  } 
  // Normal case: Use height-based mapping
  else {
    // Update the LLLE Min Req based on division
    if (division === 'A Division') {
      if (Object.prototype.hasOwnProperty.call(A_height_to_clearance, numH) && numH >= -0.5 && numH <= 145.625) {
        minreq = A_height_to_clearance[numH];
      } else if (numH >= -0.5 && numH <= 145.625) {
        minreq = findNearestKey(A_height_to_clearance, numH);
      }
    } else if (division === 'B Division') {
      if (Object.prototype.hasOwnProperty.call(B_height_to_clearance, numH) && numH >= -0.5 && numH <= 148.625) {
        minreq = B_height_to_clearance[numH];
      } else if (numH >= -0.5 && numH <= 148.4375) {
        minreq = findNearestKey(B_height_to_clearance, numH);
      }
    }
  }

  // TANGENT TRACK CALCULATIONS
  if (trackType === 'tangent') {
    // calc SE based on side of track
    if (direction === 'IN' || direction === 'inside') {
      se = (numSUPER * numH) / 56.5;
    } else if (direction === 'OUT' || direction === 'outside') {
      se = (-1 * numSUPER * numH) / 56.5;
    }

    // calc clearance based on D, se
    if (direction === 'IN' || direction === 'OUT' || 
        direction === 'inside' || direction === 'outside') {
      llleclearance = minreq + se;
    } else {
      llleclearance = minreq;
    }
  
  // CURVED TRACK CALCULATIONS
  } else {
    if (numMO !== 0) {
      r = (1.5 * 2500) / numMO;
    }

    if (!isFinite(r)) {
      r = 0;
    }

    // Calculate se based on direction and SUPER
    if (direction === 'IN' || direction === 'inside') {
      se = (numSUPER * numH) / 56.5;
    } else if (direction === 'OUT' || direction === 'outside') {
      se = (-1 * numSUPER * numH) / 56.5;
    }

    // Calculate ce and ee based on r and direction
    if (r !== 0 && division === "A Division") {
      if (direction === 'IN' || direction === 'inside') {
        ce = 1944 / r;
      } else if (direction === 'OUT' || direction === 'outside') {
        ee = 1512 / r;
      }
    } else if (r !== 0 && division === "B Division") {
      if (direction === 'IN' || direction === 'inside') {
        ce = 4374 / r;
      } else if (direction === 'OUT' || direction === 'outside') {
        ee = 2945 / r;
      }
    }
    
    // Calculate llleclearance based on direction
    if (direction === 'IN' || direction === 'OUT' || 
        direction === 'inside' || direction === 'outside') {
      llleclearance = minreq + se + ce + ee;
    } else {
      llleclearance = minreq;
    }
  }

  // For above-train calculations (GOR between 0 and -28.25), calculate clearance from H
  if (numD <= 0 && numD >= -28.25) {
    clearance = numH - llleclearance;
  } else {
    // For regular calculations, calculate clearance from D
    clearance = numD - llleclearance;
  }

  return {
    r,
    se,
    ee,
    ce,
    minreq,
    llleclearance,
    clearance
  };
};


/**
 * Reset all input values to null and calculation results to 0
 * @param setters - Object containing all state setter functions
 */
export const resetCalculationState = (setters: CalculationStateSetters): void => {
  const { 
    setH, setD, setMO, setSUPER, 
    setR, setSE, setEE, setCE, 
    setLLLEMinReq, setLLLEClearance, setClearance
  } = setters;

  // Reset inputs to null (empty state)
  setH(null);
  setD(null);
  setMO(null);
  setSUPER(null);

  // Reset all calculations to 0
  setR(0);
  setSE(0);
  setEE(0);
  setCE(0);
  setLLLEMinReq(0);
  setLLLEClearance(0);
  setClearance(0);
  // Height sufficiency check no longer needed
};


/**
 * Process null input values by converting them to 0 for calculations
 * @param value - Input value that might be null
 * @returns Processed numeric value
 */
const processInputValue = (value: number | null): number => {
  return value === null ? 0 : value;
};

/**
 * Process and validate multiple input values
 * @param inputs - Object containing input values
 * @returns Processed input values as numbers
 */
const processInputs = (inputs: { H: number | null; D: number | null; MO: number | null; SUPER: number | null }) => {
  return {
    H: processInputValue(inputs.H),
    D: processInputValue(inputs.D),
    MO: processInputValue(inputs.MO),
    SUPER: processInputValue(inputs.SUPER)
  };
};

/**
 * Main calculation update function - handles both calculate and reset operations
 * @param currentState - Current state values
 * @param setters - State setter functions
 * @param context - Calculation context (track type, division, etc.)
 */
export const updateCalculations = (
  currentState: CalculationState,
  setters: CalculationStateSetters,
  context: CalculationContext
): void => {
  const { state, H, D, MO, SUPER } = currentState;
  const { setState } = setters;

  if (state === 'results') {
    // Reset mode: go back to input state and clear all values
    setState('input');
    resetCalculationState(setters);
  } else if (state === 'input') {
    // Calculate mode: perform calculations and show results
    setState('results');

    // Process all inputs at once
    const processedInputs = processInputs({ H, D, MO, SUPER });

    // Update null inputs to show 0 in the UI
    const { setH, setD, setMO, setSUPER } = setters;
    if (H === null) setH(0);
    if (D === null) setD(0);
    if (MO === null) setMO(0);
    if (SUPER === null) setSUPER(0);

    // Calculate results using existing function
    const results = calculateResults({
      trackType: context.trackType,
      direction: context.direction,
      division: context.division,
      H: processedInputs.H,
      D: processedInputs.D,
      MO: processedInputs.MO,
      SUPER: processedInputs.SUPER,
      A_height_to_clearance: context.A_height_to_clearance,
      B_height_to_clearance: context.B_height_to_clearance,
      A_above_gor_to_height: context.A_above_gor_to_height,
      B_above_gor_to_height: context.B_above_gor_to_height
    });

    // Update state with calculated values
    const { 
      setR, setSE, setEE, setCE, 
      setLLLEMinReq, setLLLEClearance, setClearance 
    } = setters;

    setR(formatNumber(results.r, 3));
    setSE(formatNumber(results.se, 3));
    setEE(formatNumber(results.ee, 3));
    setCE(formatNumber(results.ce, 3));
    setLLLEMinReq(formatNumber(results.minreq, 3));
    setLLLEClearance(formatNumber(results.llleclearance, 3));
    setClearance(formatNumber(results.clearance, 3));
  }
};